"use client";

import { useState } from "react";
import Link from "next/link";
import { AvatarStack } from "@/components/AvatarStack";
import { TimeAgo } from "@/components/TimeAgo";
import { useConversations } from "@/lib/hooks/useConversations";
import { useChatDock } from "@/components/ChatDock";
import {
  conversationTitle,
  otherParticipants,
  personName,
} from "@/app/beskeder/participants";
import { useDismissable } from "@/lib/hooks/useDismissable";
import { ConversationMark, DmBadge } from "@/components/DmSignal";

/**
 * The mail-icon popup, a sibling of the notifications dropdown it deliberately
 * mirrors: same width, same rows, same "Se alle" footer.
 *
 * Picking a conversation opens it in a corner window rather than navigating,
 * so reading a message never costs you the page you were on.
 */
export function MessagesDropdown({
  unreadMessages,
  dmActivity,
}: {
  unreadMessages: number;
  /** A rename or a reaction — a dot, never a number. See `DmSignal`. */
  dmActivity: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useDismissable<HTMLDivElement>(open, () => setOpen(false));
  const { openChat } = useChatDock();

  // Only fetch the list once the popup has been opened — the header is on every
  // page, and the badge already comes from the cheap unread-count poll.
  const { data, isLoading } = useConversations({ enabled: open });
  const conversations = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="hidden lg:block relative" ref={ref}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="relative p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
        title="Beskeder"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="22" y1="2" x2="11" y2="13" />
          <polygon points="22 2 15 22 11 13 2 9 22 2" />
        </svg>
        <DmBadge
          count={unreadMessages}
          activity={dmActivity}
          className="absolute -top-0.5 -right-0.5"
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-surface rounded-xl border border-border shadow-lg z-50">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">
              Beskeder
            </h3>
            <Link
              href="/beskeder/ny"
              onClick={() => setOpen(false)}
              aria-label="Ny besked"
              title="Ny besked"
              className="p-1.5 -mr-1.5 text-subtle-foreground hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-muted rounded-lg transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </Link>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading && (
              <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                Henter…
              </p>
            )}

            {!isLoading && conversations.length === 0 && (
              <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                Ingen beskeder endnu
              </p>
            )}

            {conversations.slice(0, 10).map((c) => {
              const unread = c.unreadCount > 0;
              const others = otherParticipants(c.participants);
              // Never the message text before it has been accepted.
              const preview = c.isRequest
                ? "Ny besked · åbn for at acceptere"
                : c.lastMessage
                  ? `${c.lastMessage.isMine ? "Du: " : ""}${c.lastMessage.excerpt}`
                  : "Ingen beskeder";

              return (
                <div
                  key={c.id}
                  className={`group flex items-center gap-1 pr-2 hover:bg-surface-2 transition-colors ${
                    unread ? "bg-indigo-50/50 dark:bg-indigo-950/50" : ""
                  }`}
                >
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    openChat(c.id);
                  }}
                  className="flex-1 min-w-0 text-left flex items-center gap-3 pl-4 py-3"
                >
                  <AvatarStack
                    primaryUrl={others[0]?.avatar.small}
                    primaryName={personName(others[0])}
                    otherUrls={others.slice(1).map((u) => u.avatar.small)}
                    otherNames={others.slice(1).map(personName)}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm truncate text-foreground ${
                        unread ? "font-semibold" : ""
                      }`}
                    >
                      {conversationTitle(c)}
                    </p>
                    <p
                      className={`text-xs truncate mt-0.5 ${
                        c.isRequest
                          ? "text-primary"
                          : "text-subtle-foreground"
                      }`}
                    >
                      {preview}
                    </p>
                    <TimeAgo date={c.lastMessageAt} />
                  </div>
                  <ConversationMark unread={unread} activity={c.hasUnseenActivity} />
                </button>

                <Link
                  href={`/beskeder/${c.id}`}
                  onClick={() => setOpen(false)}
                  aria-label="Åbn i fuld visning"
                  title="Åbn i fuld visning"
                  className="shrink-0 p-1.5 rounded-lg text-subtle-foreground pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 pointer-fine:focus-visible:opacity-100 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                </Link>
                </div>
              );
            })}
          </div>

          <div className="border-t border-border">
            <Link
              href="/beskeder"
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 text-sm text-center text-primary hover:bg-surface-2 transition-colors"
            >
              Se alle
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
